import { useLocation, useNavigate } from '@remix-run/react';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import styled, { withTheme } from 'styled-components';
import { Routes } from '~/common/constants/routes';
import { useDevice } from '~/common/hooks/useDevice';
import useMediaQuery from '~/common/hooks/useMediaQuery';
import { isBrowser } from '~/common/utils/browser';
import { useCartSSR } from '~/recoil/useCartSSR';
import { Colors, Theme } from '~/theme';
import { colors } from '~/theme/colors';
import Header from './components/header';
import { Scrollbar } from './components/scrollbar';
import Sidebar from './components/sidebar';
import { Root } from './styles';
import useColor from '~/recoil/useColor';

interface LayoutProps {
  children: JSX.Element | JSX.Element[];
  showLogo?: boolean;
  theme?: Theme;
}

const Layout: FC<LayoutProps> = withTheme(
  ({ children, showLogo = true }: LayoutProps) => {
    const [{ count }] = useCartSSR();
    const { pathname } = useLocation();
    const { isMobile } = useMediaQuery();
    const { isTouchDevice } = useDevice();
    const navigate = useNavigate();

    const isFlashSale =
      isBrowser && window?.ENV.PUBLIC_ENABLE_FLASH_SALE === 'true';

    const showSocial =
      !(pathname === Routes.Cart || (pathname === Routes.Shop && isMobile)) &&
      !isTouchDevice;

    const fontColor =
      pathname === Routes.Home
        ? (colors.primary as Colors)
        : (colors.text as Colors);

    const [isMobileNavigationOpen, toggleMobileNavigation] =
      useState<boolean>(false);

    const rootRef = useRef<HTMLDivElement>(null);

    const { isTablet } = useMediaQuery();

    useEffect(() => {
      const allowedPathnamesForFlashSale = ['/', Routes.Sizing];
      // Force navigation back to the homepage if flashsale is enabled
      if (isFlashSale && !allowedPathnamesForFlashSale.includes(pathname)) {
        navigate('/');
      }
    }, [isFlashSale]);

    const { setColor, color } = useColor();

    useEffect(() => {
      // Reset background color if it's not a product page
      if (!pathname.includes(Routes.Product)) {
        setColor('');
      }
    }, [color, pathname]);

    const ConditionalLayout = useCallback(() => {
      if (isFlashSale) {
        return <>{children}</>;
      }

      return (
        <>
          <StyledMain background={color}>
            <Sidebar
              fontColor={fontColor}
              isMobileNavigationOpen={isMobileNavigationOpen}
              toggleMobileNavigation={toggleMobileNavigation}
              activeTab={pathname}
            />
            {children}
          </StyledMain>
          {/* Comment out for now - maybe will be needed later */}
          {/* <Footer
            isMobileNavigationOpen={isMobileNavigationOpen}
            fontColor={fontColor}
            showSocial={showSocial}
          /> */}
        </>
      );
    }, [
      isFlashSale,
      fontColor,
      isMobileNavigationOpen,
      pathname,
      showSocial,
      color,
    ]);

    return (
      <Root ref={rootRef}>
        <Header
          cartItemCount={count}
          showLogo={showLogo}
          fontColor={fontColor}
          isMobileNavigationOpen={isMobileNavigationOpen}
        />
        {ConditionalLayout()}
        {isTouchDevice || isTablet ? null : <Scrollbar rootRef={rootRef} />}
      </Root>
    );
  },
);

const StyledMain = styled.main<{ background?: string }>`
  display: flex;
  flex-direction: row;
  ${({ background }) => {
    if (background) {
      const styles = `
      * {
        color: white !important;
      }

      background ${background};
      `;

      return styles;
    }
  }};
`;

export default withTheme(Layout);
